import {
  allauth,
  CODE_EXPIRED,
  problemsOf,
  stateOf,
  type AccountProblem,
  type AccountState,
  type AllauthResponse,
} from './allauth';

export type AccountOutcome =
  | { readonly ok: true; readonly state: AccountState }
  | { readonly ok: false; readonly problems: readonly AccountProblem[] };

/** The visitor's account as the server knows it. Walking the city needs none; whatever the
 *  city keeps for a visitor belongs to the account that is signed in. */
export class Account {
  private value: AccountState = { kind: 'signedOut' };
  private readonly listeners = new Set<(state: AccountState) => void>();

  get state(): AccountState {
    return this.value;
  }

  onChange(listener: (state: AccountState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async refresh(): Promise<AccountOutcome> {
    return this.settle(await allauth.session());
  }

  async signIn(email: string, password: string): Promise<AccountOutcome> {
    return this.settle(await allauth.signIn(email, password), email);
  }

  async signUp(email: string, password: string): Promise<AccountOutcome> {
    return this.settle(await allauth.signUp(email, password), email);
  }

  async verify(code: string): Promise<AccountOutcome> {
    const response = await allauth.verify(code);
    if (response?.status === 409) {
      await this.refresh();
      return { ok: false, problems: [{ field: 'code', message: CODE_EXPIRED }] };
    }
    return this.settle(response);
  }

  async resend(): Promise<AccountOutcome> {
    const response = await allauth.resend();
    if (response?.status === 200) return { ok: true, state: this.value };
    if (response?.status === 409) {
      await this.refresh();
      return { ok: false, problems: [{ field: 'code', message: CODE_EXPIRED }] };
    }
    return { ok: false, problems: problemsOf(response) };
  }

  async signOut(): Promise<AccountOutcome> {
    return this.settle(await allauth.signOut());
  }

  /** The server has already signed this browser in: read who, rather than refuse. */
  private async settle(
    response: AllauthResponse | null,
    email: string | null = null
  ): Promise<AccountOutcome> {
    if (response?.status === 409) return this.refresh();
    const known = this.value.kind === 'verifying' ? this.value.email : null;
    const state = response ? stateOf(response, email ?? known) : null;
    if (!state) return { ok: false, problems: problemsOf(response) };
    this.set(state);
    return { ok: true, state };
  }

  private set(state: AccountState): void {
    const before = this.value;
    this.value = state;
    const email = (one: AccountState) => (one.kind === 'signedOut' ? null : one.email);
    if (before.kind === state.kind && email(before) === email(state)) return;
    for (const listener of this.listeners) listener(state);
  }
}
